import { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  openWalletDb,
  loadWallets,
  loadWatchedAddresses,
  loadSettings,
} from '@gonka-wallet/storage';
import { router } from './router.js';
import { useWalletStore } from './stores/walletStore.js';
import { useSettingsStore } from './stores/settingsStore.js';
import { initI18n } from './i18n/index.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { ToastStack } from './components/ToastStack.js';
import { WatchedAddressModal } from './components/WatchedAddressModal.js';
import { InstallPromptBanner } from './components/InstallPromptBanner.js';
import { ThemeProvider } from './providers/ThemeProvider.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  const [ready, setReady] = useState(false);
  const setWallets = useWalletStore((s) => s.setWallets);
  const setWatched = useWalletStore((s) => s.setWatched);
  const setSettings = useSettingsStore((s) => s.setAll);

  useEffect(() => {
    let cancelled = false;
    (async (): Promise<void> => {
      // 1. Open DB and hydrate stores from persisted state.
      const db = await openWalletDb();
      const [wallets, watched, settings] = await Promise.all([
        loadWallets(db),
        loadWatchedAddresses(db),
        loadSettings(db),
      ]);
      if (cancelled) return;
      setWallets(wallets);
      setWatched(watched);
      setSettings(settings);

      // One-time theme preference migration for existing users.
      // If the user has wallets but no stored theme preference, lock
      // them into 'dark' so the rebrand doesn't surprise them with a
      // light interface. New users (zero wallets) fall through to the
      // system preference set by the FOUC inline script.
      if (wallets.length > 0 && localStorage.getItem('gonka.theme') === null) {
        const { useThemeStore } = await import('./stores/themeStore.js');
        useThemeStore.getState().setPreference('dark');
      }

      // 2. Initialize i18n with the resolved locale. The Telegram SDK
      // is loaded synchronously in index.html <head>, so
      // window.Telegram?.WebApp is available if we're inside a TMA.
      const tmaLanguageCode = (
        window as unknown as {
          Telegram?: { WebApp?: { initDataUnsafe?: { user?: { language_code?: string } } } };
        }
      ).Telegram?.WebApp?.initDataUnsafe?.user?.language_code;
      await initI18n(settings.locale, tmaLanguageCode);

      if (!cancelled) setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [setWallets, setWatched, setSettings]);

  if (!ready) {
    return (
      <ThemeProvider>
        <div className="flex min-h-screen items-center justify-center bg-bg text-text-primary">
          <div className="text-lg opacity-60">Loading...</div>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          <ToastStack />
          <WatchedAddressModal />
          <InstallPromptBanner />
        </QueryClientProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}
